import type { PCMAudioManager } from '@/lib/pcmAudioManager';
import type { StreamingClient } from '@/lib/streamingClient';
import type {
  AudioQualityBinding,
  AudioQualityDisplay,
  AudioQualityEvent,
  UseAudioQualityOptions,
} from '@/types/audioQuality';
import { useCallback, useEffect, useRef, useState } from 'react';

const STALE_MS = 30_000;
const STATES = new Set(['checking', 'assessed', 'insufficient_speech', 'unavailable']);

export const useAudioQuality = ({
  streamingClientRef,
  pcmAudioManagerRef,
  isRecording,
  isPaused,
  connected,
}: UseAudioQualityOptions) => {
  const [reading, setReading] = useState<AudioQualityDisplay | null>(null);
  const qualifiedAt = useRef<number | null>(null);
  const binding = useRef<AudioQualityBinding | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const markPending = useCallback(() => {
    qualifiedAt.current = null;
    setReading((previous) => (previous ? { state: 'checking', score: null, poor: false } : null));
  }, []);

  const bind = useCallback(
    (client: StreamingClient, manager: PCMAudioManager) => {
      if (binding.current?.client === client && binding.current.manager === manager) {
        markPending();
        return;
      }
      binding.current = { client, manager };
      qualifiedAt.current = null;
      setReading(null);
      let writer: string | null = null;
      let sequence = -1;
      const retiredWriters = new Set<string>();

      client.onAudioQuality((event: AudioQualityEvent) => {
        if (!mounted.current || streamingClientRef.current !== client || pcmAudioManagerRef.current !== manager) return;
        if (event?.session_id !== client.getSessionId() || event.capture_epoch_id !== manager.captureEpochId) return;
        if (!event.writer_id || !Number.isSafeInteger(event.sequence) || event.sequence < 0 || !STATES.has(event.state))
          return;
        if (
          event.state === 'assessed' &&
          (event.score == null || !Number.isFinite(event.score) || event.score < 0 || event.score > 5)
        )
          return;
        if (retiredWriters.has(event.writer_id)) return;
        if (writer === event.writer_id && event.sequence <= sequence) return;
        if (writer && writer !== event.writer_id) {
          retiredWriters.add(writer);
          const oldestWriter = retiredWriters.values().next().value;
          if (retiredWriters.size > 16 && oldestWriter) retiredWriters.delete(oldestWriter);
        }
        writer = event.writer_id;
        sequence = event.sequence;
        qualifiedAt.current = event.state === 'assessed' ? Date.now() : null;
        setReading({
          state: event.state,
          score: event.state === 'assessed' ? event.score : null,
          poor: event.state === 'assessed' && event.poor === true && event.warnings_enabled === true,
        });
      });
    },
    [markPending, pcmAudioManagerRef, streamingClientRef]
  );

  useEffect(() => {
    if (!connected) markPending();
  }, [connected, markPending]);

  useEffect(() => {
    if (!isRecording || isPaused || reading?.state !== 'assessed' || qualifiedAt.current == null) return;
    const timeout = setTimeout(
      () => {
        if (qualifiedAt.current != null && Date.now() - qualifiedAt.current >= STALE_MS) {
          setReading({ state: 'stale', score: null, poor: false });
        }
      },
      Math.max(0, STALE_MS - (Date.now() - qualifiedAt.current))
    );
    return () => clearTimeout(timeout);
  }, [reading, isRecording, isPaused]);

  const quality: AudioQualityDisplay | null =
    !isRecording || !reading
      ? null
      : isPaused
        ? { state: 'paused', score: reading.score, poor: false }
        : !connected
          ? { state: 'checking', score: null, poor: false }
          : reading;

  return { quality, bind, markPending };
};
